import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Store, LogOut, Check, X, Star, Loader2, Sparkles, Lock, Clock, ImagePlus } from 'lucide-react';
import { partnerApi, authApi, adminApi } from '../admin/api';
import { PARTNER_CATEGORIES } from './categories';

const inputCls = 'w-full rounded-lg border border-paelbi-line bg-white px-3 py-2.5 text-sm text-paelbi-ink outline-none focus:border-paelbi-brown';
const STATUS = {
  trial: { label: 'Periodo di prova', cls: 'bg-amber-100 text-amber-800' },
  active: { label: 'Attivo', cls: 'bg-green-100 text-green-800' },
  expired: { label: 'Scaduto', cls: 'bg-red-100 text-red-700' },
  suspended: { label: 'Sospeso', cls: 'bg-gray-200 text-gray-700' },
};
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return '—'; } };

function Banner({ sub }) {
  if (sub.status === 'trial') return (
    <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="partner-trial-banner">
      <Sparkles size={22} className="text-amber-600" />
      <p className="text-sm text-paelbi-ink/80"><span className="font-semibold text-amber-700" data-testid="partner-trial-days">{sub.days_remaining} giorni</span> di prova rimanenti · scade il {fmt(sub.trial_end)}</p>
    </div>
  );
  if (sub.status === 'expired' || sub.status === 'suspended') return (
    <div className="mb-6 flex flex-col items-center gap-2 rounded-2xl bg-paelbi-ink px-6 py-8 text-center text-white" data-testid="partner-expired-banner">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10"><Lock size={22} /></div>
      <h2 className="font-display text-xl">Il tuo periodo di prova è terminato</h2>
      <p className="max-w-md text-sm text-white/75">Scegli un piano per continuare a farti trovare dai turisti.</p>
    </div>
  );
  return (
    <div className="mb-6 flex items-center gap-3 rounded-2xl border border-green-200 bg-green-50 p-4 text-sm" data-testid="partner-active-banner">
      <Clock size={18} className="text-green-600" />
      <span className="text-paelbi-ink/80">Piano <span className="font-semibold capitalize">{sub.plan}</span> attivo · rinnovo il {fmt(sub.subscription_end)}</span>
    </div>
  );
}

export function PartnerDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState('');
  const [busyPlan, setBusyPlan] = useState('');
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    partnerApi.me().then((d) => {
      setData(d);
      setForm({
        business_name: d.profile.business_name || '', category: d.profile.category || 'Ristoranti',
        phone: d.profile.contacts?.phone || '', email: d.profile.contacts?.email || '',
        website: d.profile.website || '', address: d.profile.address || '',
        description: d.profile.description || '', logo_url: d.profile.logo_url || null,
      });
    }).catch(() => navigate('/admin/login', { replace: true }));
  }, [navigate]);
  useEffect(() => { load(); }, [load]);

  const logout = async () => { try { await authApi.logout(); } catch { /* ignore logout errors; navigate away regardless */ } navigate('/admin/login', { replace: true }); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const r = await adminApi.uploadMedia(file); setForm((f) => ({ ...f, logo_url: r.url })); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const save = async () => {
    setSaved('');
    const d = await partnerApi.updateProfile(form);
    setData(d); setSaved('Profilo salvato');
    setTimeout(() => setSaved(''), 1500);
  };

  const choose = async (plan) => {
    setBusyPlan(plan);
    try { const r = await partnerApi.selectPlan(plan); setData((prev) => ({ ...prev, subscription: r.subscription })); }
    finally { setBusyPlan(''); }
  };

  if (!data || !form) return <div className="flex min-h-screen items-center justify-center bg-paelbi-bg text-paelbi-ink/50">Caricamento...</div>;
  const sub = data.subscription;
  const st = STATUS[sub.status] || STATUS.expired;

  return (
    <div className="min-h-screen bg-paelbi-bg" data-testid="partner-dashboard">
      <header className="border-b border-paelbi-line bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2"><Store size={22} className="text-paelbi-brown" />
            <div className="leading-tight"><p className="font-display text-lg text-paelbi-ink">Domira Partner</p>
              <p className="text-[10px] uppercase tracking-widest text-paelbi-ink/50">Dashboard attività</p></div></div>
          <button onClick={logout} data-testid="partner-logout" className="inline-flex items-center gap-1.5 text-sm text-paelbi-brown hover:underline"><LogOut size={15} />Esci</button>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-6 py-8">
        <Banner sub={sub} />

        {/* Subscription status */}
        <div className="mb-8 rounded-2xl border border-paelbi-line bg-white p-6" data-testid="partner-subscription-status">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div><p className="text-sm text-paelbi-ink/50">Piano attuale</p>
              <p className="font-display text-2xl capitalize text-paelbi-ink" data-testid="partner-current-plan">{sub.plan}</p></div>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${st.cls}`}>{st.label}</span>
          </div>
          {(sub.status === 'trial' || sub.status === 'active') && (
            <p className="mt-3 border-t border-paelbi-line pt-3 text-sm text-paelbi-ink/60">
              Giorni rimanenti: <span className="font-semibold text-paelbi-brown">{sub.days_remaining ?? '—'}</span> · scadenza {fmt(sub.status === 'trial' ? sub.trial_end : sub.subscription_end)}
            </p>
          )}
        </div>

        {/* Profile */}
        <div className="mb-8 rounded-2xl border border-paelbi-line bg-white p-6">
          <h2 className="font-display text-xl text-paelbi-ink">Dati attività</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="block"><span className="mb-1 block text-sm font-medium text-paelbi-ink/80">Nome attività</span>
              <input value={form.business_name} onChange={set('business_name')} className={inputCls} data-testid="partner-edit-name" /></label>
            <label className="block"><span className="mb-1 block text-sm font-medium text-paelbi-ink/80">Categoria</span>
              <select value={form.category} onChange={set('category')} className={inputCls} data-testid="partner-edit-category">
                {PARTNER_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
            <label className="block"><span className="mb-1 block text-sm font-medium text-paelbi-ink/80">Telefono</span>
              <input value={form.phone} onChange={set('phone')} className={inputCls} data-testid="partner-edit-phone" /></label>
            <label className="block"><span className="mb-1 block text-sm font-medium text-paelbi-ink/80">Email</span>
              <input value={form.email} onChange={set('email')} className={inputCls} data-testid="partner-edit-email" /></label>
            <label className="block"><span className="mb-1 block text-sm font-medium text-paelbi-ink/80">Sito web</span>
              <input value={form.website} onChange={set('website')} className={inputCls} data-testid="partner-edit-website" /></label>
            <label className="block"><span className="mb-1 block text-sm font-medium text-paelbi-ink/80">Indirizzo</span>
              <input value={form.address} onChange={set('address')} className={inputCls} data-testid="partner-edit-address" /></label>
            <label className="block sm:col-span-2"><span className="mb-1 block text-sm font-medium text-paelbi-ink/80">Descrizione</span>
              <textarea rows={3} value={form.description} onChange={set('description')} className={inputCls} data-testid="partner-edit-description" /></label>
            <div className="sm:col-span-2 flex items-center gap-4">
              {form.logo_url && <img src={form.logo_url} alt="logo" className="h-16 w-16 rounded-lg border border-paelbi-line object-contain bg-white p-1" />}
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-btn border border-paelbi-brown/40 px-4 py-2 text-sm font-semibold text-paelbi-ink hover:border-paelbi-brown">
                <ImagePlus size={16} />{uploading ? 'Caricamento...' : 'Logo'}
                <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="partner-logo-upload" />
              </label>
            </div>
          </div>
          <div className="mt-5 flex items-center gap-3">
            <button onClick={save} data-testid="partner-save-profile" className="rounded-btn bg-paelbi-brown px-5 py-2.5 text-sm font-semibold text-white hover:bg-paelbi-brown-dark">Salva</button>
            {saved && <span className="text-sm text-green-700" data-testid="partner-saved-msg">{saved}</span>}
          </div>
        </div>

        {/* Plans */}
        <h2 className="mb-4 font-display text-xl text-paelbi-ink">Piano scelto</h2>
        <div className="grid gap-6 md:grid-cols-3">
          {sub.catalog.map((p) => {
            const current = sub.status === 'active' && sub.plan === p.id;
            const featured = !!p.badge;
            return (
              <div key={p.id} data-testid={`partner-plan-${p.id}`}
                className={`relative flex flex-col rounded-2xl border bg-white p-6 ${featured ? 'border-paelbi-brown shadow-card ring-1 ring-paelbi-brown/30' : 'border-paelbi-line'}`}>
                {p.badge && <span className="absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 rounded-full bg-paelbi-brown px-3 py-1 text-xs font-semibold text-white"><Star size={12} /> Più scelto</span>}
                <p className="font-display text-xl text-paelbi-ink">{p.name}</p>
                <p className="mt-2"><span className="font-display text-3xl text-paelbi-ink">{p.currency}{p.price}</span><span className="text-sm text-paelbi-ink/50"> {p.period}</span></p>
                <ul className="mt-4 flex-1 space-y-2 text-sm">
                  {p.includes.map((f) => <li key={f} className="flex items-start gap-2 text-paelbi-ink/80"><Check size={16} className="mt-0.5 shrink-0 text-green-600" />{f}</li>)}
                  {p.excludes.map((f) => <li key={f} className="flex items-start gap-2 text-paelbi-ink/40"><X size={16} className="mt-0.5 shrink-0" />{f}</li>)}
                </ul>
                <button disabled={current || busyPlan === p.id} onClick={() => choose(p.id)} data-testid={`partner-select-${p.id}`}
                  className={`mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-btn text-sm font-semibold disabled:opacity-60 ${featured ? 'bg-paelbi-brown text-white hover:bg-paelbi-brown-dark' : 'border border-paelbi-brown/40 text-paelbi-ink hover:border-paelbi-brown'}`}>
                  {busyPlan === p.id && <Loader2 size={16} className="animate-spin" />}{current ? 'Piano attuale' : p.cta}
                </button>
              </div>
            );
          })}
        </div>
        <p className="mt-4 text-center text-xs text-paelbi-ink/40">I pagamenti online saranno disponibili a breve. La selezione attiva subito il piano.</p>
      </div>
    </div>
  );
}

export default PartnerDashboard;
